import type {
  GridRowParams,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import React, { type MouseEvent, type ReactNode, useState } from "react";
import { useNonConflictingTheme } from "src/lib/emotion/use-non-conflicting-theme.ts";
import {
  type ContextItemModel,
  DataGridContextMenu,
} from "src/components/DataGrid/DataGridContextMenu.tsx";
import { css, ThemeProvider } from "@emotion/react";
import type { DataGridContextMenuItem } from "src/components/DataGrid/DataGrid.tsx";
import {
  flip,
  FloatingPortal,
  offset,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { Text } from "src/components/Text/Text.tsx";
import { FLOATING_PORTAL_ROOT_ID } from "src/common/constants.ts";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { useTranslation } from "react-i18next";

export type DataGridContextMenuProps<R extends GridValidRowModel = any> = {
  selectedRow?: NoInfer<R> | undefined;
  defaultSelectedRow?: NoInfer<R> | undefined;
  customContent?: ReactNode;
  isLoading?: boolean;
  onSelectedRowChange?: (row: NoInfer<R> | undefined) => void;
  LoadingComponent?: React.ComponentType;
  items: DataGridContextMenuItem<R>[];
};

export const useDataGridRowContextMenu = <
  R extends GridValidRowModel = GridValidRowModel,
>({
  selectedRow: selectedRowProp,
  defaultSelectedRow: defaultSelectedRowProp,
  onSelectedRowChange,
  isLoading = false,
  LoadingComponent = CheckingState,
  items,
}: DataGridContextMenuProps<R>) => {
  // todo: support uncontrolled behaviour
  const setSelectedRow = (row: R | undefined) => {
    onSelectedRowChange?.(row);
  };

  const theme = useNonConflictingTheme();

  const selectedRow = selectedRowProp ?? defaultSelectedRowProp;

  const [currentConfirmAction, setCurrentConfirmAction] = useState<
    string | undefined
  >(undefined);

  const getItems = (row: R): ContextItemModel[] => {
    return items
      .filter((item) => {
        if (item.hide) {
          return !item.hide(row);
        }
        return true;
      })
      .map((item) => ({
        label: item.label,
        icon: item.icon,
        danger: item.danger,
        onClick: () => {
          if (item.ConfirmComponent) {
            setCurrentConfirmAction(item.key);
            floating.update();
          } else {
            item?.onSelect?.(row);
          }
        },
      }));
  };

  const renderCustomContent = (row: R): ReactNode => {
    if (isLoading) {
      return (
        <ThemeProvider theme={theme}>
          <LoadingComponent />
        </ThemeProvider>
      );
    }

    const item = items.find((item) => item.key === currentConfirmAction);

    if (!item?.ConfirmComponent) {
      return undefined;
    }

    return (
      <ThemeProvider theme={theme}>
        <item.ConfirmComponent
          onCancel={() => {
            floating.update();
            setCurrentConfirmAction(undefined);
          }}
          onClose={() => {
            floating.update();
            setCurrentConfirmAction(undefined);
          }}
          row={row}
          onConfirm={() => {
            item.onConfirm?.(row);
            closeContextMenu();
          }}
        />
      </ThemeProvider>
    );
  };

  const onOpenChange = (open: boolean) => {
    if (!open) {
      closeContextMenu();
    }
  };

  const open = !!selectedRow;

  const items2 = selectedRow ? getItems(selectedRow) : [];
  const customContent = selectedRow && renderCustomContent(selectedRow);

  const floating = useFloating({
    open,
    placement: "bottom-start",
    onOpenChange: onOpenChange,
    middleware: [offset({ alignmentAxis: 5 }), flip()],
  });
  const { getFloatingProps } = useInteractions([
    useDismiss(floating.context, {
      //  enabled: !modalOpen,
    }),
  ]);

  const getElement = () => {
    return (
      <>
        {open && (
          <FloatingPortal id={FLOATING_PORTAL_ROOT_ID}>
            <div
              ref={floating.refs.setFloating}
              {...getFloatingProps()}
              style={floating.floatingStyles}
            >
              <DataGridContextMenu
                onClose={() => onOpenChange(false)}
                customContent={customContent}
                items={items2}
              />
            </div>
          </FloatingPortal>
        )}
      </>
    );
  };

  const closeContextMenu = () => {
    setSelectedRow(undefined);
    setCurrentConfirmAction(undefined);
  };

  const openContextMenu = (params: GridRowParams, event: MouseEvent) => {
    const { clientX, clientY } = event;
    floating.refs.setPositionReference({
      getBoundingClientRect: () => ({
        width: 0,
        height: 0,
        x: clientX,
        y: clientY,
        top: clientY,
        left: clientX,
        right: clientX,
        bottom: clientY,
      }),
    });
    setSelectedRow(params.row);
  };

  return {
    Element: getElement(),
    open: openContextMenu,
    close: closeContextMenu,
    selectedRow,
    setSelectedRow,
  };
};

const CheckingState = () => {
  const { t } = useTranslation();
  return (
    <div css={checkingStateStyles}>
      <Text>
        {t("advices.pleaseWait", {
          ns: TranslationNamespaceEnum.Common,
        })}
      </Text>
    </div>
  );
};

const checkingStateStyles = css`
  padding: 10px;
  max-width: 200px;
`;
